import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import BaseModal from './BaseModal'
import { setHorseModal, setHorseToEdit as horseTEdit } from '../../store/reducers/modal.reducer'
import { Alert, Autocomplete, Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, useMediaQuery, useTheme } from '@mui/material'
import { t } from 'i18next'
import { get, groupBy, isEmpty, orderBy, sortBy, toNumber } from 'lodash'
import { string, z } from 'zod'
import { useHorses } from '../../hooks/useHorses'
import { LoadingButton } from '@mui/lab'
import { TUser } from '../../shared/types/user.type'
import { toast } from 'react-toastify'
import { THorse } from '../../shared/types/horse.type'
import { useQueryClient } from 'react-query'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { GENDERS } from '../../enums/gender.enums'
import { TBreeds } from '../../shared/types/breed.type'

import dayjs from 'dayjs'

type HorseFormProps = {
  toggleMicroshipHorse: () => void,
  onCancel: () => void,
  onSaveMicroship: (chipValue: string) => void,
  loading: boolean
}

type AddHorseChipProps = {
  breeds: TBreeds[],
  loading: boolean,
  horseFromEdit: boolean,
  onCancel: () => void,
  defaultHorse: THorse | null,
  onSave: (data: Partial<THorse>) => void
  onPatch: (id: number, data: Partial<THorse>) => void,
  tempChip: string | null
}



function AddHorseModal() {

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const open = useSelector<RootState>(state => state.modals.horseModal) as boolean
  const horseEdit = useSelector<RootState>(state => state.modals.horseToEdit) as THorse | null
  let auth = useSelector<RootState>(state => state.user.user) as TUser | null
  const [breeds, setBreeds] = useState<TBreeds[]>([])
  const dispatch = useDispatch()
  const [hasChip, setHasChip] = useState(true)
  const query = useQueryClient()
  const [horseToEdit, setHorseToEdit] = useState<THorse | null>(null)
  const [tempChip, setTempChip] = useState<string | null>(null)
  const [horseFromEdit, setHorseFromEdit] = useState(false)

  const { checkMicroship, saveHorseTrainer, isSavingHorseTrainer, getBreeds, saveHorse, updateHorse, reloadHorses } = useHorses(false, auth?.['@id'])
  const onCancel = () => {
    setHorseToEdit(null)
    setHorseFromEdit(false)
    dispatch(horseTEdit(null))
    setHasChip(true)
    dispatch(setHorseModal(false))
    setTempChip(null)
  }

  useEffect(() => {
    if (!horseEdit) return
    setHorseToEdit(horseEdit)
    setHasChip(false)
    setHorseFromEdit(true)
  }, [horseEdit])

  const toggleMicroshipHorse = () => {
    setHasChip(false)
  }



  useEffect(() => {
    if (!open) return
    getBreeds.mutateAsync({ inList: true })
      .then(({ data }) => {
        setBreeds(get(data, 'hydra:member', []))
      }).catch(err => {
        console.log({ err });

      })
  }, [open])

  const saveTrainer = async (data: THorse) => {
    if (auth?.['@id']) {
      try {
        await saveHorseTrainer.mutateAsync({ "horse": data['@id'], "trainer": auth['@id'] })
        toast(t('addHorse:success', { horse: data.name }), { type: 'success' })

      } catch (err) {

      } finally {
        onCancel()
        query.refetchQueries(['horses'])
      }
    }
  }

  const onSave = (chipValue: string) => {
    checkMicroship.mutateAsync(chipValue)
      .then(async ({ data }) => {
        //check if horse has name or no
        if (!data.needs_to_be_named) {
          setHorseToEdit(null)
          await saveTrainer(data)
        } else {
          setHorseToEdit(data)
          setHasChip(false)
        }


      }).catch(err => {
        //in case if not found rfid
        if (err.status == 404) {
          setTempChip(chipValue)
        }
        setHasChip(false)
        setHorseToEdit(null)

      })
  }

  const saveHorseData = (data: Partial<THorse>) => {
    saveHorse.mutateAsync(data)
      .then(result => {
        saveTrainer(result.data)
      }).catch(err => {
        toast(t('error:generic'), { type: 'error' })
      })

  }

  const patchHorse = (id: number, data: Partial<THorse>) => {
    updateHorse.mutateAsync({ id, data })
      .then(result => {
        toast(t('addHorse:editSuccess'), { type: 'success' })
        !horseFromEdit ? saveTrainer(result.data) :
          onCancel()
        query.refetchQueries(['horses'])
      }).catch(err => {
        toast(t('error:generic'), { type: 'error' })
      })

  }

  if (!open) return null

  return (
    <BaseModal width={matches ? 450 : 320} title={horseToEdit ? 'horse.edit' : 'addHorse:addHorse'} open={open} handleClose={onCancel}>
      {
        hasChip ?
          <AddHorseWithChip loading={checkMicroship.isLoading || isSavingHorseTrainer} onCancel={onCancel} toggleMicroshipHorse={toggleMicroshipHorse} onSaveMicroship={onSave}></AddHorseWithChip>
          : <AddHorseWithoutChip
            horseFromEdit={horseFromEdit}
            defaultHorse={horseToEdit}
            onSave={saveHorseData}
            onPatch={patchHorse}
            tempChip={tempChip}
            loading={saveHorse.isLoading || updateHorse.isLoading || saveHorseTrainer.isLoading}
            onCancel={onCancel}
            breeds={breeds} />
      }
    </BaseModal>
  )
}

function AddHorseWithChip(props: HorseFormProps) {
  const [err, setErr] = useState(false)
  const [chipValue, setChipValue] = useState("")
  const handleSubmit = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value
    if (value.length <= 15) {
      setChipValue(value)
    }
  }


  useEffect(() => {
    if (chipValue) {
      const validateChipSchema = z.string().length(15)

      try {
        validateChipSchema.parse(chipValue);
        setErr(false)
      } catch (err) {
        setErr(true)
      }



    }
  }, [chipValue])


  return (
    <Box display={'flex'} flexDirection={'column'} gap={1}>
      <TextField
        error={err}
        helperText={chipValue.length + ('/15')}
        value={chipValue}
        onChange={handleSubmit}
        fullWidth label={t('addHorse:microchipNumber')} type='number' ></TextField>
      <Button onClick={props.toggleMicroshipHorse} variant='text' sx={{ alignSelf: 'end', }} >{t('addHorse:horseNoMicrochip')}</Button>
      <Box display={'flex'} justifyContent={'end'} gap={1}>
        <LoadingButton loading={props.loading} onClick={props.onCancel} variant='outlined' color='error'>{t('button:cancel')}</LoadingButton>
        <LoadingButton loading={props.loading} disabled={err || chipValue.length === 0} onClick={() => props.onSaveMicroship(chipValue)} variant='contained'>{t('button:save')}</LoadingButton>
      </Box>
    </Box>)
}

function AddHorseWithoutChip({ breeds, loading, onCancel, onSave, onPatch, defaultHorse, horseFromEdit, tempChip }: AddHorseChipProps) {


  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Partial<THorse>>({
    defaultValues: {
      name: defaultHorse?.name,
      birthday: defaultHorse?.birthday ? dayjs(defaultHorse.birthday) : undefined,
      father_name: defaultHorse?.father_name,
      father_mother_name: defaultHorse?.father_mother_name,
      gender: defaultHorse?.gender,
      mother_name: defaultHorse?.mother_name,
      breed: typeof defaultHorse?.breed === 'string' ? defaultHorse?.breed : defaultHorse?.breed?.['@id']

    }
  })


  const getBreedsByPriority = (breeds: TBreeds[], priorities: string[]) => {
    return breeds.sort((a, b) => {
      const indexA = priorities.indexOf(a.ifce_code);
      const indexB = priorities.indexOf(b.ifce_code);

      // If both are in the priority list, sort by index in that list
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only a is in the list, it comes first
      if (indexA !== -1) return -1;

      // If only b is in the list, it comes first
      if (indexB !== -1) return 1;

      // Otherwise, maintain original order (or sort alphabetically if you want)
      return 0;
    });
  }


  const breeds_in_list = useMemo(() => {
    if (isEmpty(breeds)) return {
      breeds: [],
      length: 0
    }


    let inList = breeds.filter(breed => breed.in_list)

    return {
      breeds: [...getBreedsByPriority(inList, ["PS", "TF", "AR", "AA", "TE"]), ...breeds.filter(breed => !breed.in_list)],
      length: breeds.filter(breed => breed.in_list).length - 1
    }
  }, [breeds])


  const onSubmit: SubmitHandler<Partial<THorse>> = (data) => {
    defaultHorse && defaultHorse.id ? onPatch(defaultHorse.id, data) : onSave({ ...data, ...(tempChip ? { rfid: String(tempChip) } : {}) })
  }




  return (
    <Stack direction={'column'} gap={1} >
      <Alert severity='info'>{t('editMicrochipInfoText')}</Alert>

      {/* horsefrom edit is a bool value to declare is the horse is from edit or no */}
      {/* default horse is the horse we want to edit */}
      {horseFromEdit ?
        <></> :
        defaultHorse ?
          <Alert severity='warning'>{t('addHorse:microchipNotFound')}</Alert>
          : !tempChip ? <Alert severity='error'>{t('addHorse:warningNoMicrochip')}</Alert> : <></>
      }

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={'column'} gap={2} mt={1}>

          {
            ((defaultHorse && defaultHorse?.rfid) || tempChip) && <TextField disabled
              label={t('addHorse:microchipNumber')}
              defaultValue={defaultHorse?.rfid ?? tempChip}></TextField>
          }
          <TextField
            {...register('name', { required: true })} fullWidth label={t('addHorse:horseName')}></TextField>

          <DatePicker
            value={getValues('birthday')}
            slotProps={{ textField: { helperText: t('addHorse:birthdateExplanation') } }}
            views={['year', 'month']} label={t('addHorse:birthdate')}
            format='YYYY / MM'
            onChange={(value) => {
              setValue('birthday', dayjs(String(value)))
            }}

          />

          <TextField  {...register('mother_name', { required: false })} fullWidth label={t('horse.damname')}></TextField>
          <TextField  {...register('father_name', { required: false })} fullWidth label={t('horse.sirename')}></TextField>
          <TextField  {...register('father_mother_name', { required: false })} fullWidth label={t('horse.siredamname')}></TextField>

          <FormControl fullWidth >
            <InputLabel id="demo-simple-select-label">{t('gender')}</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"

              label={t('gender')}
              onChange={e => setValue('gender', String(e.target.value))}
              defaultValue={getValues('gender')}
            >
              {
                GENDERS.map(gender => (
                  <MenuItem key={gender.value} value={gender.value}>{t(gender.key)}</MenuItem>
                ))
              }

            </Select>
          </FormControl>
          <FormControl fullWidth >
            <InputLabel id="demo-simple-select-label">{t('breed:breed')}</InputLabel>

            <Select
              defaultValue={getValues('breed')}
              onChange={(_, value) => setValue('breed', _.target.value)}
              label={t('breed:breed')}
            >
              {
                breeds_in_list.breeds.map(breed => (
                  <MenuItem key={breed['@id']} value={breed['@id']}>{breed.in_list ? t('breed:' + breed.ifce_code) : breed.name}</MenuItem>
                ))
              }
            </Select>

          </FormControl>
          <Box display={'flex'} justifyContent={'end'} gap={1}>
            <LoadingButton loading={loading} onClick={onCancel} variant='outlined' color='error'>{t('button:cancel')}</LoadingButton>
            <LoadingButton loading={loading} type='submit' variant='contained'>{t('button:save')}</LoadingButton>
          </Box>
        </Stack>
      </form >
    </Stack >)
}
export default AddHorseModal